import { cn } from "@/lib/utils";
import { ExternalLinkIcon, type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

export type StatTileTone = "emerald" | "sky" | "amber" | "indigo" | "fuchsia";

const TONE_CLASSES: Record<
  StatTileTone,
  { gradient: string; badge: string }
> = {
  emerald: {
    gradient: "from-emerald-500/5",
    badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
  sky: {
    gradient: "from-sky-500/5",
    badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  },
  amber: {
    gradient: "from-amber-500/5",
    badge: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  indigo: {
    gradient: "from-indigo-500/5",
    badge: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
  },
  fuchsia: {
    gradient: "from-fuchsia-500/5",
    badge: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400",
  },
};

type StatTileProps = {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  tone: StatTileTone;
  valueClassName?: string;
  /** Under the value: a marker, a hint. */
  footer?: ReactNode;
  /** Makes the whole tile a link to its source (opens in a new tab). */
  href?: string;
  hrefLabel?: string;
  className?: string;
};

/**
 * Bordered tile with a gradient accent, tinted icon badge, label and value.
 * Used by the stock card and anywhere a small themed KPI tile is needed.
 */
export default function StatTile({
  icon: Icon,
  label,
  value,
  tone,
  valueClassName,
  footer,
  href,
  hrefLabel,
  className,
}: StatTileProps) {
  const classes = TONE_CLASSES[tone];
  return (
    <div
      className={cn(
        "group relative flex items-center gap-3 rounded-xl border border-border bg-gradient-to-br to-transparent px-4 py-3",
        classes.gradient,
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded-lg shrink-0",
          classes.badge,
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
        <span className={cn("text-base font-bold truncate", valueClassName)}>
          {value}
        </span>
        {footer}
      </div>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={hrefLabel}
          className="absolute inset-0 rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <ExternalLinkIcon
            aria-hidden
            className="absolute right-2 top-2 size-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100"
          />
        </a>
      )}
    </div>
  );
}
